import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { useStatusData } from '@/hooks/useStatusData';
import { themeVar } from '@/styles/theme';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AssetClassRow } from './assetClassRow';

const calculateCapacity = (
  row: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number],
  hasCommitmentPlans: boolean,
  targetOCRatio: number | null,
  targetSubAssetClasses: boolean
) => {
  if (hasCommitmentPlans && targetSubAssetClasses && 'subAssetClasses' in row) {
    const subAssetClassesSum = row.subAssetClasses.reduce((sum, subAssetClass) => {
      return sum + (subAssetClass.illiquid ? subAssetClass.pacingTarget?.remainingPacingTarget || 0 : subAssetClass.capacity);
    }, 0);
    return subAssetClassesSum;
  }
  if (hasCommitmentPlans && targetOCRatio === null) {
    return row.illiquid ? row.pacingTarget?.remainingPacingTarget || 0 : row.capacity;
  }
  return row.capacity;
};

type SimpleTableProps = {
  assetClassTableData: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'];
  hasCommitmentPlans: boolean;
  targetOCRatio: number | null;
};

const SimpleTable = ({ assetClassTableData, hasCommitmentPlans, targetOCRatio }: SimpleTableProps) => {
  const { data: statusData } = useStatusData();

  const [assetClassTargets, setAssetClassTargets] = useState<InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets']>(
    []
  );

  useEffect(() => {
    if (!assetClassTableData) return;

    setAssetClassTargets(assetClassTableData);
  }, [assetClassTableData]);

  const totalRow = useMemo(() => {
    return assetClassTargets.reduce(
      (acc, row) => ({
        total: 'Total',
        target: acc.target + row.percentage,
        capacity: acc.capacity + calculateCapacity(row, hasCommitmentPlans, targetOCRatio, statusData?.target_sub_asset_classes || false)
      }),
      {
        total: 'Total',
        target: 0,
        capacity: 0
      }
    );
  }, [assetClassTargets, hasCommitmentPlans, targetOCRatio]);

  return (
    <SimpleTableWrap>
      <SimpleTableHeader>
        <HeaderItem>ASSET CLASS</HeaderItem>
        <HeaderItem>TARGET</HeaderItem>
        <HeaderItem>CAPACITY</HeaderItem>
      </SimpleTableHeader>
      <SimpleTableBody>
        {assetClassTargets?.map((assetClass) => (
          <AssetClassRow
            key={assetClass.id}
            assetClass={assetClass}
            targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
            hasCommitmentPlans={hasCommitmentPlans}
            targetOCRatio={targetOCRatio}
          />
        ))}
      </SimpleTableBody>
      <SimpleTableFooter>
        <FooterItem>TOTAL</FooterItem>
        <FooterItem>{totalRow.target.toFixed(2)}%</FooterItem>
        <FooterItem>
          {!hasCommitmentPlans && targetOCRatio === null ? <>--</> : <FormattingTooltip zIndex={1000}>{totalRow.capacity}</FormattingTooltip>}
        </FooterItem>
      </SimpleTableFooter>
    </SimpleTableWrap>
  );
};

const SimpleTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterItem = styled.div`
  width: 33%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  color: ${themeVar('font.strong')};
`;

const SimpleTableFooter = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  justify-content: space-between;
  background-color: ${themeVar('layer.2')};
  padding: 5px 24px;
`;

const HeaderItem = styled.div`
  width: 33%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  color: ${themeVar('header.action')};
`;

const SimpleTableHeader = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  justify-content: space-between;
  background-color: ${themeVar('header.base')};
  padding: 5px 24px;
`;

const SimpleTableWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export default SimpleTable;
